import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { FilterSection, RangeSlider, Spinner, RadioList, MultiSelectFilter, AsyncMultiSelectFilter, SegmentedControl } from './primitives';
import { useAsyncLabeledSelect } from './useAsyncLabeledSelect';
import { getConciergeBrands, getConciergeShoppers } from '../../../api/noir';
import {
  TIERS,
  USER_TYPE_OPTIONS,
  NOIR_STATUSES,
  PURCHASE_OPTIONS,
  ENGAGEMENT_TYPES,
  CODE_LOGIC_OPTIONS
} from './constants';

// Editable score bound — type an exact value (e.g. 72), committed on blur/Enter, clamped to 0–100
// and to the other bound so min ≤ max. Drafts locally so mid-typing values aren't clamped away.
const ScoreBoundInput = ({ value, min, max, onCommit }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const n = parseInt(draft, 10);
    if (isNaN(n)) return setDraft(String(value));
    const clamped = Math.min(Math.max(n, min), max);
    // Re-sync the draft here too: when clamping lands on the current value, the [value] effect
    // won't fire (no change) and the box would keep showing the out-of-range typed text.
    setDraft(String(clamped));
    onCommit(clamped);
  };
  return (
    <input
      type='number'
      className='nb-score-input'
      min={min}
      max={max}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => e.key === 'Enter' && e.target.blur()}
    />
  );
};

const CohortBuilder = ({ filters, promoCodes, editors, loading, onPreview, onReset }) => {
  const {
    tiers,
    setTiers,
    score,
    setScore,
    noirInteraction,
    setNoirInteraction,
    selectedCodes,
    setSelectedCodes,
    codeLogic,
    setCodeLogic,
    noirEngageWindow,
    setNoirEngageWindow,
    noirEngageType,
    setNoirEngageType,
    purchaseFilter,
    setPurchaseFilter,
    curatorIds,
    setCuratorIds,
    circleCuratorIds,
    setCircleCuratorIds,
    brandIds,
    setBrandIds,
    shopperIds,
    setShopperIds,
    userType,
    setUserType,
    onWaitlist,
    setOnWaitlist,
    excludeRecentDays,
    setExcludeRecentDays,
    hasFilters
  } = filters;

  const hasRecentExclusion = parseInt(excludeRecentDays, 10) > 0;

  // Curator Affinity: one editor multi-select whose meaning is set by a mode toggle — "Received
  // edit" writes to curatorIds, "In circles" writes to circleCuratorIds (separate backend filters).
  // The mode is derived from whichever id list is populated so saved-filter hydration (duplicate
  // flow) lands on the right tab; local state only decides the tab while nothing is selected.
  // Switching modes carries the current selection over to the other filter.
  const [affinityModeChoice, setAffinityModeChoice] = useState('edit');
  const affinityMode = curatorIds.length > 0 ? 'edit' : circleCuratorIds.length > 0 ? 'circles' : affinityModeChoice;
  const handleAffinityModeChange = mode => {
    setAffinityModeChoice(mode);
    if (mode === 'circles' && curatorIds.length > 0) {
      setCircleCuratorIds(curatorIds);
      setCuratorIds([]);
    } else if (mode === 'edit' && circleCuratorIds.length > 0) {
      setCuratorIds(circleCuratorIds);
      setCircleCuratorIds([]);
    }
  };
  const affinityIds = affinityMode === 'edit' ? curatorIds : circleCuratorIds;
  const setAffinityIds = affinityMode === 'edit' ? setCuratorIds : setCircleCuratorIds;

  // Brand + shopper filters are server-backed typeaheads (the catalogs are too large to ship up
  // front). Each keeps its selected ids as the source of truth and caches id→label for the chips —
  // see useAsyncLabeledSelect. Brand label is just the name; shopper label is "Name · @username · #id".
  const brand = useAsyncLabeledSelect({
    ids: brandIds,
    setIds: setBrandIds,
    fetchFn: getConciergeBrands,
    labelFn: b => b.name,
    name: 'brand'
  });
  const shopper = useAsyncLabeledSelect({
    ids: shopperIds,
    setIds: setShopperIds,
    fetchFn: getConciergeShoppers,
    labelFn: s => [s.name, s.username && `@${s.username}`, `#${s.id}`].filter(Boolean).join(' · '),
    name: 'shopper'
  });

  return (
    <div className='nb-builder'>
      <div className='nb-builder-header'>
        <span className='nb-builder-label'>DEFINE COHORT</span>
        {(hasFilters || hasRecentExclusion) && (
          <button className='nb-clear-filters' onClick={onReset}>
            Clear all
          </button>
        )}
      </div>

      <FilterSection label='User Type'>
        <RadioList name='nb-ut' options={USER_TYPE_OPTIONS} value={userType} onChange={setUserType} />
      </FilterSection>

      <FilterSection label='Noir Waitlist'>
        <label className='nb-recency-toggle'>
          <input type='checkbox' checked={onWaitlist} onChange={() => setOnWaitlist(p => !p)} />
          <span>
            Only shoppers on the <strong>Noir waitlist</strong> — Silver/Bronze (with no code requests left, if applicable)
          </span>
        </label>
        {onWaitlist && <p className='nb-hint'>Overrides the tier selection below — the waitlist is Silver &amp; Bronze only.</p>}
      </FilterSection>

      <FilterSection label='Shopper Tier'>
        <div className='nb-pills'>
          {TIERS.map(t => {
            const sel = tiers.includes(t);
            return (
              <button
                key={t}
                className={cn('nb-pill', { active: sel })}
                onClick={() => setTiers(p => (sel ? p.filter(x => x !== t) : [...p, t]))}
              >
                {t}
              </button>
            );
          })}
        </div>
      </FilterSection>

      <FilterSection label='Shopper Score' collapsible defaultOpen>
        <div className='nb-score-header'>
          <span className='nb-score-label'>Score range</span>
          <span className='nb-score-value'>
            <ScoreBoundInput value={score[0]} min={0} max={score[1]} onCommit={v => setScore([v, score[1]])} />
            –
            <ScoreBoundInput value={score[1]} min={score[0]} max={100} onCommit={v => setScore([score[0], v])} />
          </span>
        </div>
        <RangeSlider value={score} onChange={setScore} />
        <p className='nb-hint'>Drag the handles or type exact bounds — e.g. 72–74 within Gold</p>
      </FilterSection>

      <FilterSection label='Noir Engagement'>
        <p className='nb-sub-hint'>Interaction status</p>
        <RadioList name='nb-ns' options={NOIR_STATUSES} value={noirInteraction} onChange={setNoirInteraction} />
        <p className='nb-sub-hint tight spaced'>Active in Noir within X days</p>
        <div className='nb-engage-row'>
          <span className='nb-engage-text'>Within last</span>
          <input
            type='number'
            className='nb-inline-input'
            value={noirEngageWindow}
            onChange={e => setNoirEngageWindow(e.target.value)}
            placeholder='–'
          />
          <span className='nb-engage-text'>days</span>
        </div>
        <p className='nb-sub-hint tight'>Engagement type</p>
        <SegmentedControl options={ENGAGEMENT_TYPES} value={noirEngageType} onChange={setNoirEngageType} />
        {noirEngageType === 'approved_not_converted' && (
          <p className='nb-hint'>Approved a delivered edit but never purchased through a Noir collection (purchase check is all-time)</p>
        )}
      </FilterSection>

      <FilterSection label='Promo Code Usage' collapsible defaultOpen={false}>
        <p className='nb-sub-hint'>Shoppers who used a code</p>
        <MultiSelectFilter
          className='nb-code-select with-bottom-gap'
          placeholder='Select codes…'
          emptyText='No promo codes configured yet.'
          options={promoCodes.map(c => ({ value: c, label: c }))}
          value={selectedCodes.map(c => ({ value: c, label: c }))}
          onChange={setSelectedCodes}
        />
        {selectedCodes.length > 0 && (
          <div>
            <p className='nb-sub-hint tight'>Match logic</p>
            <SegmentedControl options={CODE_LOGIC_OPTIONS} value={codeLogic} onChange={setCodeLogic} />
          </div>
        )}
      </FilterSection>

      <FilterSection label='Purchase Activity' collapsible defaultOpen={false}>
        <RadioList name='nb-pf' options={PURCHASE_OPTIONS} value={purchaseFilter} onChange={setPurchaseFilter} />
      </FilterSection>

      <FilterSection label='Curator Affinity' collapsible defaultOpen={false}>
        <p className='nb-sub-hint'>Affinity type</p>
        <SegmentedControl
          options={[
            ['edit', 'Received edit'],
            ['circles', 'In circles']
          ]}
          value={affinityMode}
          onChange={handleAffinityModeChange}
        />
        <p className='nb-sub-hint spaced'>
          {affinityMode === 'edit'
            ? 'Shoppers who received a Noir edit (delivered collection) from any selected editor'
            : 'Shoppers who have any selected Noir editor in one of their circles'}
        </p>
        <MultiSelectFilter
          placeholder='Select editors…'
          emptyText='No editors found.'
          options={editors.map(e => ({ value: e.id, label: e.name || e.username || `#${e.id}` }))}
          value={editors
            .filter(e => affinityIds.includes(e.id))
            .map(e => ({ value: e.id, label: e.name || e.username || `#${e.id}` }))}
          onChange={setAffinityIds}
        />
      </FilterSection>

      <FilterSection label='Purchased From Brand' collapsible defaultOpen={false}>
        <p className='nb-sub-hint'>Shoppers who bought from any selected brand (ShopMy, Gmail &amp; Plaid · active orders, all time)</p>
        <AsyncMultiSelectFilter
          placeholder='Search brands…'
          loadOptions={brand.loadOptions}
          value={brand.value}
          onChange={brand.handleChange}
        />
      </FilterSection>

      <FilterSection label='Specific Shoppers' collapsible defaultOpen={false}>
        <p className='nb-sub-hint'>Search by name, username, or user ID · overrides all other filters</p>
        <AsyncMultiSelectFilter
          placeholder='Search shoppers…'
          loadOptions={shopper.loadOptions}
          value={shopper.value}
          onChange={shopper.handleChange}
        />
        {shopperIds.length > 0 && <p className='nb-uid-hint'>{shopperIds.length} shopper(s) · other filters overridden</p>}
      </FilterSection>

      <FilterSection label='Recent Message Exclusion' collapsible defaultOpen={false}>
        <div className='nb-engage-row'>
          <span className='nb-engage-text'>Exclude shoppers messaged within the last</span>
          <input
            type='number'
            min='1'
            className='nb-inline-input'
            value={excludeRecentDays}
            onChange={e => setExcludeRecentDays(e.target.value)}
            placeholder='–'
          />
          <span className='nb-engage-text'>days</span>
        </div>
        <p className='nb-hint'>
          {hasRecentExclusion
            ? `Shoppers who received any Noir SMS in the last ${parseInt(excludeRecentDays, 10)} days will be excluded.`
            : 'Leave blank to send regardless of recency — recently-messaged shoppers are flagged in the preview instead.'}
        </p>
      </FilterSection>

      <div className='nb-preview-cta'>
        {!hasRecentExclusion && (
          <p className='nb-no-exclusion-flag'>
            ⚠ No recent-message exclusion set — shoppers messaged within the last 14 days will still receive this broadcast
          </p>
        )}
        <button onClick={() => onPreview()} disabled={!hasFilters || loading}>
          {loading ? (
            <>
              <Spinner color='#fff' /> Previewing cohort…
            </>
          ) : (
            'Preview Cohort'
          )}
        </button>
        {!hasFilters && <p className='nb-preview-cta-hint'>Set at least one filter to preview</p>}
      </div>
    </div>
  );
};

export default CohortBuilder;
